package com.trevorwiebe.timesheet.admin.loadrate.data

import com.trevorwiebe.timesheet.admin.loadrate.data.dto.LoadRateDto
import com.trevorwiebe.timesheet.admin.loadrate.data.dto.toLoadRate
import com.trevorwiebe.timesheet.admin.loadrate.domain.LoadRateRepository
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRate
import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.data.Utils.errorWrapper
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.TSResult
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.CollectionReference
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map
import kotlin.time.Clock
import kotlinx.serialization.Serializable

/** Document shape written for a load rate; the Firestore doc id is the identity. */
@Serializable
private data class LoadRateWriteDto(
    val rateCategory: String,
    val rateType: String,
    val unit: String?,
    val perUnit: Int,
    val flatRate: Int,
    val pickupLocationId: String,
    val dropLocationId: String,
    val lastUpdated: String,
)

class LoadRateRepositoryImpl(
    private val firebaseDatabase: Firebase,
    private val coreRepository: CoreRepository,
) : LoadRateRepository {

    override suspend fun getLoadRates(): Flow<TSResult> {
        val orgIdResult = coreRepository.getOrganizationId()
        if (orgIdResult.error != null) return flow { emit(orgIdResult) }
        val orgId = orgIdResult.data as String

        val flow = loadRatesCollection(orgId)
            .snapshots
            .map { snapshot ->
                snapshot.documents
                    .map { doc -> doc.data<LoadRateDto>().toLoadRate(id = doc.id) }
                    .sortedWith(compareBy({ it.rateCategory }, { it.rateType }))
            }
            .map { TSResult(data = it) }

        return FirestoreListenerRegistry.registerFlow(flow)
    }

    override suspend fun addLoadRate(loadRate: LoadRate): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            loadRatesCollection(orgId).add(loadRate.toWriteDto())
            TSResult(data = "Load rate added successfully")
        }
    }

    override suspend fun updateLoadRate(loadRate: LoadRate): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            loadRatesCollection(orgId)
                .document(loadRate.id)
                .set(loadRate.toWriteDto(), merge = true)
            TSResult(data = "Load rate updated successfully")
        }
    }

    private fun LoadRate.toWriteDto() = LoadRateWriteDto(
        rateCategory = rateCategory.name,
        rateType = rateType.name,
        unit = unit?.name,
        perUnit = perUnit,
        flatRate = flatRate,
        pickupLocationId = pickupLocationId,
        dropLocationId = dropLocationId,
        lastUpdated = Clock.System.now().toString(),
    )

    private fun loadRatesCollection(orgId: String): CollectionReference =
        firebaseDatabase.firestore
            .collection("organizations")
            .document(orgId)
            .collection("loadRates")
}
